import React from 'react';

import PropTypes from 'prop-types';
import {Form, FormikProps} from 'formik';
import {CallMade} from '@material-ui/icons';
import {Box, Divider, FormControlLabel, Grid} from '@material-ui/core';

import {FieldShape} from 'commons/shapes';
import {InferPropTypes} from 'commons/types/Types';
import {
    AddressServiceRequestExtended,
    EfeCatalogValue,
    EfeResponse,
    Field,
    SepomexCatalog,
    SepomexCatalogResponse,
} from 'commons/models';
import {EfeButtonPrimary, EfeCheckbox, EfeFormSection, EfeSupportMessage} from 'components';
import {CatalogServices} from 'services';
import {Status, SnackbarVariants, Messages, CONSTANT_VALUES, HttpCodes} from 'commons/constants';
import {useSnackbar} from 'notistack';
import {useSpinner} from 'hooks';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {Constants} from 'commons';
import {fieldCatalogGetter} from 'hooks/FlowHook';

InnerFormLocationInfo.propTypes = {
    showAdditionalAddress: PropTypes.bool.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
};
const ATTR_NULL = 'attrNameNull';

function InnerFormLocationInfo(
    props: InferPropTypes<
        typeof InnerFormLocationInfo.propTypes & FormikProps<AddressServiceRequestExtended>
    >
) {
    const {showAdditionalAddress, values, errors, fields, isSubmitting, setFieldValue} = props;
    const {foreign} = useMenuContext();
    const {enqueueSnackbar} = useSnackbar();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [checked, setChecked] = React.useState<boolean>(false);
    const [tempFields, setTempFields] = React.useState<Array<Field>>(
        fieldCatalogGetter(
            Constants.TEMP_ADDRESS_FIELDS.filter((field) => ATTR_NULL !== field.attrName)
        ).sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))
    );

    function setValues(data: SepomexCatalog, zipCode: string) {
        if (zipCode) {
            setFieldValue('zipCode', zipCode);
        }
        if (data && data.municipalityKey) {
            setFieldValue('municipalityKey', data.municipalityKey);
        }
        if (data && data.municipality) {
            setFieldValue('municipalityDescription', data.municipality);
        }
        if (data && data.stateKey) {
            setFieldValue('stateKey', data.stateKey);
        }
        if (data && data.state) {
            setFieldValue('stateDescription', data.state);
        }
        setFieldValue('countryKey', CONSTANT_VALUES.MexicoId);
    }

    function setTempSepomexValues(data: SepomexCatalog, zipCode: string) {
        if (zipCode) {
            setFieldValue('zipCodeNat', zipCode);
        }
        if (data && data.municipalityKey) {
            setFieldValue('municipalityKeyNat', data.municipalityKey);
        }
        if (data && data.municipality) {
            setFieldValue('municipalityDescriptionNat', data.municipality);
        }
        if (data && data.stateKey) {
            setFieldValue('stateKeyNat', data.stateKey);
        }
        if (data && data.state) {
            setFieldValue('stateDescriptionNat', data.state);
        }
        setFieldValue('countryKeyNat', CONSTANT_VALUES.MexicoId);
    }

    function resetValues(tempFlag: boolean) {
        if (tempFlag) {
            setFieldValue('municipalityKeyNat', '');
            setFieldValue('municipalityDescriptionNat', '');
            setFieldValue('stateKeyNat', '');
            setFieldValue('stateDescriptionNat', '');
            setFieldValue('countryKeyNat', '');
        } else {
            setFieldValue('municipalityKey', '');
            setFieldValue('municipalityDescription', '');
            setFieldValue('stateKey', '');
            setFieldValue('stateDescription', '');
            setFieldValue('countryKey', '');
        }
    }

    function setCatalogValues(data: Array<SepomexCatalog>, zipCode: string, tempFlag: boolean) {
        const colony: Array<EfeCatalogValue> = [];
        const state: Array<EfeCatalogValue> = [];
        let colonyItem: EfeCatalogValue;
        let stateItem: EfeCatalogValue;
        data.map((item: SepomexCatalog, index: number) => {
            if (!colonyItem || colonyItem.id !== item.settlementCpconsId) {
                colonyItem = {
                    id: item.settlementCpconsId,
                    order: index,
                    value: item.settlement,
                    status: Status.Active,
                };
                colony.push({...colonyItem});
            }
            if (!stateItem || stateItem.id !== item.stateKey) {
                stateItem = {
                    id: item.stateKey,
                    order: index,
                    value: item.state,
                    status: Status.Active,
                };
                state.push({...stateItem});
            }
        });
        if (fields && fields.length && !tempFlag) {
            fields[fields.findIndex((field) => field.attrName === 'colonyKey')].catalogValues = [
                ...colony,
            ];
            fields[fields.findIndex((field) => field.attrName === 'stateKey')].catalogValues = [
                ...state,
            ];
            if (data && data.length > 0) {
                setValues(data[0], zipCode);
            }
        } else {
            setTempFields([]);
            const fields = [...tempFields];
            fields[fields.findIndex((field) => field.attrName === 'stateKeyNat')].catalogValues = [
                ...state,
            ];
            fields[fields.findIndex((field) => field.attrName === 'colonyKeyNat')].catalogValues = [
                ...colony,
            ];
            if (data && data.length > 0) {
                setTempSepomexValues(data[0], zipCode);
            }
            setTempFields([...fields]);
        }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        setChecked(event.target.checked);
    }

    async function searchZipCode(value: string, tempFlag: boolean) {
        if (value && value.length === 5) {
            setIsLoading(true);
            try {
                const sepomexData: EfeResponse<SepomexCatalogResponse> =
                    await CatalogServices.getSepomexCatalog(value);
                if (sepomexData.data && sepomexData.data.data && sepomexData.data.data.length > 0) {
                    setCatalogValues([...sepomexData.data.data], value, tempFlag);
                } else {
                    setCatalogValues([], value, tempFlag);
                    resetValues(tempFlag);
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
                setIsLoading(false);
            } catch (error: any) {
                resetValues(tempFlag);
                setIsLoading(false);
                setCatalogValues([], value, tempFlag);
                if (error && error.code < HttpCodes.INTERNAL_SERVER_ERROR) {
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.WARNING,
                    });
                } else {
                    enqueueSnackbar(`${Messages.SEPOMEX_ERROR}${value}`, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
            }
        }
    }

    React.useEffect(() => {
        if (values.zipCodeNat && values.zipCodeNat.length) {
            setChecked(true);
        }
    }, []);

    React.useEffect(() => {
        if (values.zipCode && values.zipCode.length === 5 && !foreign) {
            searchZipCode(values.zipCode, false);
        }
        if (values.zipCodeNat && values.zipCodeNat.length === 5 && foreign) {
            searchZipCode(values.zipCodeNat, true);
        }
    }, [values.zipCode, values.zipCodeNat]);

    const NationalForm = (
        <>
            <Divider variant='middle' />
            <EfeFormSection
                fields={tempFields}
                values={values}
                errors={{...errors}}
                setFieldValue={setFieldValue}
            />
        </>
    );

    /* eslint-disable react/jsx-handler-names */
    return (
        <Form noValidate>
            {isLoading && useSpinner(isLoading)}
            {fields && fields.length > 0 && (
                <Box m={2}>
                    <EfeSupportMessage />
                    <EfeFormSection
                        fields={fields}
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                    />

                    {showAdditionalAddress && (
                        <FormControlLabel
                            control={
                                <EfeCheckbox
                                    name='checked'
                                    id='checked'
                                    onChange={handleChange}
                                    checked={checked}
                                />
                            }
                            label={'Domicilio en M\u00E9xico'}
                        />
                    )}
                    {checked && NationalForm}
                    <Grid container direction='row' justifyContent='flex-end'>
                        <Box pt={6} width={160}>
                            <EfeButtonPrimary
                                fullWidth
                                disabled={isSubmitting}
                                type='submit'
                                endIcon={<CallMade />}>
                                {'OK'}
                            </EfeButtonPrimary>
                        </Box>
                    </Grid>
                </Box>
            )}
        </Form>
    );
}

export default InnerFormLocationInfo;
